// prop-types is a library for typechecking of props
import React from 'react';
import PropTypes from 'prop-types';
import { useSpring, animated, config } from '@react-spring/web';

// @mui material components
import Icon from '@mui/material/Icon';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

// Timeline context
import { useTimeline } from 'examples/Timeline/context';

// Custom styles for the TimelineItem
import timelineItem from 'layouts/aaro/fastPrice/views/stockPopover/TimelineItem/styles.js';

function TimelineItem({ color, icon, title, dateTime, description, lastItem }) {
    const isDark = useTimeline();
    const [isHovered, setIsHovered] = React.useState(false);

    // Hover animation
    const hoverAnimation = useSpring({
        transform: isHovered ? 'translateX(5px) scale(1.02)' : 'translateX(0px) scale(1)',
        boxShadow: isHovered ? '0 4px 20px rgba(0,0,0,0.15)' : '0 2px 10px rgba(0,0,0,0.05)',
        config: config.gentle,
    });

    // Icon animation
    const iconAnimation = useSpring({
        transform: isHovered ? 'scale(1.1) rotate(5deg)' : 'scale(1) rotate(0deg)',
        config: config.wobbly,
    });

    return (
        <animated.div
            style={hoverAnimation}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <MDBox position="relative" mb={3} sx={(theme) => timelineItem(theme, { lastItem, isDark })}>
                <MDBox
                    component={animated.div}
                    style={iconAnimation}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    bgColor={color}
                    color="white"
                    width="2rem"
                    height="2rem"
                    borderRadius="50%"
                    position="absolute"
                    top="8%"
                    left="2px"
                    zIndex={2}
                    sx={{ fontSize: ({ typography: { size } }) => size.sm }}
                >
                    <Icon fontSize="inherit">{icon}</Icon>
                </MDBox>
                <MDBox ml={5.75} pt={description ? 0.7 : 0.5} lineHeight={0} maxWidth="30rem">
                    <MDTypography variant="button" fontWeight="medium" color={isDark ? 'white' : 'dark'}>
                        {title}
                    </MDTypography>
                    <MDBox mt={0.5}>
                        <MDTypography variant="caption" fontWeight="bold" color={isDark ? 'secondary' : 'text'}>
                            {dateTime}
                        </MDTypography>
                    </MDBox>
                    <MDBox mt={2} mb={1.5}>
                        {description ? (
                            <MDTypography variant="button" color={isDark ? 'white' : 'dark'}>
                                {description}
                            </MDTypography>
                        ) : null}
                    </MDBox>
                </MDBox>
            </MDBox>
        </animated.div>
    );
}

// Setting default values for the props of TimelineItem
TimelineItem.defaultProps = {
    color: 'info',
    lastItem: false,
    description: '',
};

// Typechecking props for the TimelineItem
TimelineItem.propTypes = {
    color: PropTypes.oneOf(['primary', 'secondary', 'info', 'success', 'warning', 'error', 'dark', 'light']),
    icon: PropTypes.node.isRequired,
    title: PropTypes.string.isRequired,
    dateTime: PropTypes.string.isRequired,
    description: PropTypes.string,
    lastItem: PropTypes.bool,
};

export default TimelineItem;
